import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { api } from "../../api";
import {
  IQueryPredictiveOrder,
  IQueryPredictiveOrderExecutions,
} from "../../interfaces/IQuery";

export enum ECategoryPredictive {
  copom = "copom",
  dolar = "dolar",
  ibov = "ibov",
  acoes = "acoes",
}

export enum ETypePredictive {
  MARKET_PROTECTION = "MARKET_PROTECTION",
  LIMIT = "LIMIT",
}

export enum ETypeInstrument {
  SELL = "SELL",
  BUY = "BUY",
}

export enum EInstrumentType {
  increase = "increase",
  decrease = "decrease",
  neutral = "neutral",
}

export enum EValidityType {
  TODAY = "TODAY",
  UNTIL_CANCEL = "UNTIL_CANCEL",
  UNTIL_DATE = "UNTIL_DATE",
}

export interface IListInfinitePredictivesParams {
  page_size?: number;
  page: number;
  category?: string;
  white_label_id?: number | null;
}
export interface IListPredictivesResponse {
  data: IListPredictive[];
  totalElements: number;
  totalPages: number;
}

export interface IListPredictive {
  id: number;
  title: string;
  expiration: string;
  category: string;
  type: string;
  created_at: string;
  updated_at: string;
  icon: any;
  instruments: IInstrument[];
  instruments_count: number;
}

export interface IInstrument {
  id: number;
  symbol: string;
  bid_price: number;
  bid_price_formatted: string;
  ask_price: number;
  ask_price_formatted: string;
  premium: number;
  label: string;
  direction: EInstrumentType;
  calculated_ask: number;
  calculated_ask_formatted: string;
}

export const getInfinitePredictives = async (
  params: IListInfinitePredictivesParams,
  pageParam: number = 1,
  signal?: AbortSignal
) => {
  const { data } = await api.get<IListPredictivesResponse>(
    "web/predictive/products",
    {
      signal,
      params: {
        pageSize: 12,
        page: pageParam,
        ...(params.category && { category: params.category }),
        ...(params.white_label_id && {
          whitelabelId: params.white_label_id,
        }),
      },
    }
  );

  return {
    data: data.data,
    nextPage: pageParam + 1,
    hasMore: data.totalElements > data.data.length, // Determina se há mais dados
  };
};
export const useInfinitePredictives = (
  params: IListInfinitePredictivesParams
) => {
  return useInfiniteQuery(
    ["Predictives", params],
    ({ pageParam = 1, signal }) =>
      getInfinitePredictives(params, pageParam, signal),
    {
      getNextPageParam: (lastPage) =>
        lastPage.hasMore ? lastPage.nextPage : undefined,
    }
  );
};

export interface IPostPredictiveOrder {
  predictive_instrument_id: number;
  quantity: number | "";
  type: ETypePredictive;
  price: number;
  side: ETypeInstrument;
  validity?: string;
  expire_date?: string;
}

export const postPredictiveOrder = async (data: IPostPredictiveOrder) => {
  const response = await api.post(`/web/predictive/orders`, data);
  return response;
};

export interface IPredictiveProductInstrument {
  id: number;
  symbol: string;
  predictive_product_id: number;
  emphasis: boolean;
  created_at: string;
  updated_at: string;
  percentage: number;
  premium: number;
  level1: {
    bid_price: number;
    ask_price: number;
  };
  level1_formatted: {
    bid_price: string;
    ask_price: string;
  };
  label: string;
  direction: EInstrumentType;
  calculated_ask: number;
  calculated_ask_formatted: string;
}

export interface IPredictiveProduct {
  id: number;
  title: string;
  expiration: string;
  category: string;
  type: string;
  created_at: string;
  updated_at: string;
  icon: string;
  instruments: IPredictiveProductInstrument[];
  instruments_count: number;
}

export interface IGetPredictiveProductParams {
  id: number;
  whitelabelId?: number | null;
}

export const getPredictiveProduct = async (
  params: IGetPredictiveProductParams,
  signal?: AbortSignal
) => {
  const { data } = await api.get<IPredictiveProduct>(
    `web/predictive/products/${params.id}`,
    {
      signal,
      params: {
        ...(params.whitelabelId && { whitelabelId: params.whitelabelId }),
      },
    }
  );

  return data;
};

export const usePredictiveProduct = (params: IGetPredictiveProductParams) => {
  return useQuery({
    queryKey: ["PredictiveProduct", params.id],
    queryFn: async ({ signal }) => getPredictiveProduct(params, signal),
    enabled: !!params.id,
  });
};

export enum EPredictiveOrderStatus {
  NONE = "NONE", // Nenhum
  ACTIVE = "ACTIVE", // Ativa
  PENDING = "PENDING", // Ordem Pendente
  PENDING_MODIFICATION = "PENDING_MODIFICATION", // Modificação Pendente
  PENDING_CANCELLATION = "PENDING_CANCELLATION", // Cancelamento Pendente
  RESERVED = "RESERVED", // Reservada
  ACCEPTED = "ACCEPTED", // Aceita
  SUSPENDED = "SUSPENDED", // Suspensa
  PARTIALLY_EXECUTED = "PARTIALLY_EXECUTED", // Parcialmente Executada - funciona
  EXECUTED = "EXECUTED", // Totalmente Executada
  CANCELLED = "CANCELLED", // Cancelada
  MODIFIED = "MODIFIED", // Modificada - funciona
  REJECTED = "REJECTED", // Rejeitada
  EXPIRED = "EXPIRED", // Expirada
  PREVIOUS = "PREVIOUS", // Anterior - funciona
  WAITING_MARKET = "WAITING_MARKET", // Aguardando Mercado - funciona
  WAITING_FOR_SHOT = "WAITING_FOR_SHOT", // Aguardando Disparo - funciona
  FIRED = "FIRED", // Disparada
}

export interface IListPredictiveOrder {
  id: number;
  sending_time: string;
  category: string;
  order_side: ETypeInstrument;
  percentage: number;
  expiration_product: string;
  quantity: number;
  probability: any;
  total: number;
  validity: string;
  order_status: EPredictiveOrderStatus;
  order_type: ETypePredictive;
  reject_code: string;
  reject_desc: string;
  help_reject_url: string;
  decision: number | null;
}

export interface IResponsePredictiveOrder {
  data: any[];
  totalElements: number;
  totalPages: number;
  lastUpdated: Date;
}

export const getPredictiveOrder = async (
  { page, pageSize, orderBy, sortOrder, id }: IQueryPredictiveOrder,
  signal?: AbortSignal
) => {
  let params = new URLSearchParams();
  if (!!page) params.append("page", page.toString());
  if (!!pageSize) params.append("pageSize", pageSize.toString());
  if (!!orderBy) params.append("orderBy", orderBy);
  if (!!sortOrder) params.append("sortOrder", sortOrder);

  const response = await api.get<IResponsePredictiveOrder>(
    !!id ? `/web/predictive/orders/${id}` : `/web/predictive/orders`,
    {
      signal,
      params,
    }
  );
  const serverTimestamp =
    response.headers["last-modified"] || new Date().toISOString();
  const returnData: IResponsePredictiveOrder = {
    ...response.data,
    lastUpdated: serverTimestamp,
  };

  return returnData;
};

export const usePredictiveOrder = (params: IQueryPredictiveOrder) => {
  return useQuery({
    queryKey: ["PredictiveOrder", params],
    queryFn: async ({ signal }) => {
      const data = await getPredictiveOrder(params, signal);
      return data;
    },
    refetchInterval: 1000 * 60 * 5,
    refetchIntervalInBackground: true,
    keepPreviousData: true,
  });
};

export interface IPutPredictiveOrder {
  id: number;
  quantity: number;
  price: number;
}

export const putPredictiveOrder = async (data: IPutPredictiveOrder) => {
  const { id, ...dataWithoutId } = data;
  const response = await api.put(`/web/predictive/orders/${id}`, dataWithoutId);
  return response.data;
};

export const cancelPredictiveOrder = async (id: number) => {
  const response = await api.post(`/web/predictive/orders/${id}/cancel`);
  return response.data;
};

export const getExecutionsPredictiveOrder = async (
  { page, pageSize, orderBy, sortOrder, id }: IQueryPredictiveOrderExecutions,
  signal?: AbortSignal
) => {
  let params = new URLSearchParams();
  if (!!page) params.append("page", page.toString());
  if (!!pageSize) params.append("pageSize", pageSize.toString());
  if (!!orderBy) params.append("orderBy", orderBy);
  if (!!sortOrder) params.append("sortOrder", sortOrder);

  const response = await api.get<IResponsePredictiveOrder>(
    `/web/predictive/orders/${id}/executions`,
    {
      signal,
      params,
    }
  );
  const serverTimestamp =
    response.headers["last-modified"] || new Date().toISOString();
  const returnData: IResponsePredictiveOrder = {
    ...response.data,
    lastUpdated: serverTimestamp,
  };

  return returnData;
};

export const useExecutionsPredictiveOrder = (
  params: IQueryPredictiveOrderExecutions
) => {
  return useQuery({
    queryKey: ["PredictiveOrderExecutions", params],
    queryFn: async ({ signal }) => {
      const data = await getExecutionsPredictiveOrder(params, signal);
      return data;
    },
    refetchInterval: 1000 * 60 * 5,
    refetchIntervalInBackground: true,
    keepPreviousData: true,
    enabled: !!params.id,
  });
};

export interface IPredectiveOrderById {
  id: number;
  user_id: number;
  predictive_instrument_id: number;
  broker_integration_id: number;
  sending_time: string;
  customer_account: string;
  clorderid: string;
  origclorderid: string;
  symbol: string;
  quantity: number;
  price: number;
  order_side: ETypeInstrument;
  order_type: number;
  validity: number;
  expire_date: any;
  order_status: number;
  minimum_quantity: number;
  max_floor_quantity: number;
  reject_code: number;
  reject_desc: string;
  execid: string;
  execution_type: number;
  executed_quantity: number;
  executed_price: number;
  transaction_time: string;
  suitability: any;
  suitability_expired: any;
  session_id: any;
  predictive_instrument: PredictiveInstrument;
}

export interface PredictiveInstrument {
  id: number;
  symbol: string;
  predictive_product_id: number;
  instrument_id: number;
  route_id: number;
  strike: number;
  premium: number;
  emphasis: boolean;
  created_at: string;
  updated_at: string;
  percentage: number;
  predictive_product: PredictiveProduct;
}

export interface PredictiveProduct {
  id: number;
  white_label_id: number;
  title: string;
  expiration: string;
  category: string;
  type: string;
  icon_id: number;
  active: boolean;
  decision: any;
  created_at: string;
  updated_at: string;
}

export const getPredictiveOrderById = async (
  id: number,
  signal?: AbortSignal
) => {
  const response = await api.get<IPredectiveOrderById>(
    `/web/predictive/orders/${id}`,
    {
      signal,
    }
  );

  return response;
};

export const usePredictiveOrderById = (id: number) => {
  return useQuery({
    queryKey: ["PredictiveOrder"],
    queryFn: async ({ signal }) => {
      const data = await getPredictiveOrderById(id, signal);
      return data;
    },
    refetchInterval: 1000 * 60 * 5,
    refetchIntervalInBackground: true,
    keepPreviousData: true,
    enabled: !!id,
  });
};
